import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import Group from 'components/utils/Group'
import { EmptyArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import { useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

interface Props {
  onSelect?: (option: any) => void
  affiliationOptions?: Array<App.AgentHubAffiliationOption>
  loading?: boolean
}

const StyledGroup = styled(Group)`
  padding: ${rem(8)};
  padding-top: 0;
`

function AgentHubAffiliationAutocomplete({
  onSelect = noop,
  affiliationOptions = EmptyArray,
  loading,
}: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchPhrase, setSearchPhrase] = useState<string>('')
  const [affiliationId, setAffiliationId] = useState<string | null>(null)
  const [filteredOptions, setFilteredOptions] = useState<Array<App.AgentHubAffiliationOption>>(affiliationOptions)
  const [isFocused, setIsFocused] = useState(false)

  const noResultsFound = filteredOptions.length < 1

  const onSearchPhrasePerformed = (evt: React.FocusEvent<HTMLInputElement>) => {
    evt.preventDefault()
    evt.stopPropagation()

    if (!affiliationId && !isFocused) {
      setSearchPhrase('')
    }
  }

  const onItemSelect = (value: App.AgentHubAffiliationOption) => {
    setAffiliationId(value.id)
    setSearchPhrase(value.name)
    onSelect(value)
    setIsFocused(false)
  }

  const handleOnFocus = () => {
    setSearchPhrase('')
    setIsFocused(true)
    setAffiliationId(null)
  }

  const handleClickOutside = () => {
    setIsFocused(false)
  }

  useEffect(() => {
    if (searchPhrase?.length > 0) {
      setFilteredOptions(affiliationOptions.filter(option => option.name.toLowerCase().includes(searchPhrase?.toLowerCase())))
      return
    }

    setFilteredOptions(affiliationOptions)
  }, [setFilteredOptions, searchPhrase, affiliationOptions])

  useEffect(() => {
    setSearchPhrase('')
    setAffiliationId(null)
  }, [setSearchPhrase, affiliationOptions])

  useEffect(() => {
    if (!isFocused && !affiliationId) {
      setSearchPhrase('')
    }
  }, [isFocused, affiliationId])

  return <>
    <HiddenInput name="affiliationId" value={affiliationId ?? ''} />
    <TextInput
      label={intl.formatMessage({
        id: 'dv8cyT6_rPgutxugNfgO',
        defaultMessage: 'Affiliation',
      })}
      autoComplete="off"
      onChange={(e) => setSearchPhrase(e.currentTarget.value)}
      onBlur={(e) => onSearchPhrasePerformed(e)}
      endIcon={<LineSearchIcon />}
      ref={inputRef}
      disabled={loading}
      startIcon={loading && <LoadingSpinner />}
      placeholder={intl.formatMessage({
        id: '175kK9PjG1ZfI_aDGF_C',
        defaultMessage: 'Affiliation',
      })}
      onFocus={handleOnFocus}
      required
      value={searchPhrase}
    />
    {inputRef?.current && (
      <DropdownList
        size="fill-anchor"
        open={isFocused}
        anchorRef={inputRef}
        triggerRef={inputRef}
        onClose={handleClickOutside}
      >
        <div>
          {filteredOptions.map(option => (
            <ClickableListItem
              key={option.id}
              selected={option.id === affiliationId}
              onClick={() => onItemSelect(option)}
              size="medium"
              title={option.name}
            />
          ))}
        </div>

        {noResultsFound && (
          <StyledGroup direction="vertical" gap={4}>
            <BodyText variant="medium" colour="light-foreground-alt">
              <FormattedMessage
                id="9WC_cAnlBv9iLY2CjxMg"
                defaultMessage="Sorry, we couldn’t find results for <strong>{searchPhrase}</strong>."
                
                values={{
                  searchPhrase,
                  strong: (chunks: React.ReactNode) => (
                    <BodyText
                      colour="normal-contrast"
                      as="span"
                      variant="medium"
                      weight="bold"
                    >
                      {chunks}
                    </BodyText>
                  ),
                }}
              />
            </BodyText>
            <BodyText variant="medium" colour="light-foreground-alt">
              {intl.formatMessage({
                id: '9zckiuCm7XUevk1XgVtG',
                defaultMessage: 'Try a different search phrase',
              })}
            </BodyText>
          </StyledGroup>
        )}

      </DropdownList>
    )}
  </>
}

export default AgentHubAffiliationAutocomplete
